import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'debounce';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useTranslations } from 'next-intl';
import { useLazyQuery } from '@apollo/client';
import { AddressSuggestQuery } from '@/query';

// Minimum length of address that autocomplete API can work with
const MIN_ADDR_LENGTH = 2;

const useAddressSearch = () => {
  const [address, setAddress] = useState('');
  const reqRef = useRef(null);
  const [suggestAddress, { data, loading, error }] =
    useLazyQuery(AddressSuggestQuery);

  const addresses = data?.addressAutocomplete || [];

  const onRequestAddress = useCallback(
    (addr) => {
      if (reqRef.current) {
        reqRef.current.abort();
        reqRef.current = null;
      }
      if (!addr || addr.length < MIN_ADDR_LENGTH) return Promise.resolve(null);
      const aborter = new AbortController();
      reqRef.current = aborter;
      return suggestAddress({
        variables: { name: addr },
        context: { fetchOptions: { signal: aborter.signal } },
      });
    },
    [suggestAddress],
  );

  const isAbortError = error
    ? error.name.toLowerCase() !== 'aborterror'
    : false;

  return {
    address,
    setAddress,
    onRequestAddress,
    addresses,
    loading,
    error: isAbortError ? undefined : error,
  };
};

function useAddressDropdown(address = '') {
  const handleClickOutside = (_e) => {
    setOpened(false);
  };
  const containerRef = useDetectClickOutside({
    onTriggered: handleClickOutside,
  });
  const [opened, setOpened] = useState(false);
  const onOpenDropdown = useCallback(
    (forceOpen) => {
      if (typeof forceOpen === 'boolean') {
        setOpened(forceOpen);
        return;
      }
      setOpened(address.length >= MIN_ADDR_LENGTH);
    },
    [address],
  );
  const onCloseDropdown = useCallback(() => setOpened(false), []);
  return {
    containerRef,
    opened,
    onOpenDropdown,
    onCloseDropdown,
  };
}

export function useAddressAutocomplete(initialAddress = '') {
  const {
    address,
    setAddress,
    onRequestAddress,
    addresses,
    loading,
    error: apolloError,
  } = useAddressSearch(initialAddress);
  const { containerRef, opened, onOpenDropdown, onCloseDropdown } =
    useAddressDropdown(address);
  const [isAddressInvalid, setIsAddressInvalid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const onValidateAddress = useCallback(
    (currentValue) => {
      const currentAddress = currentValue || address;
      const found = address
        ? addresses.find((addr) => addr === currentAddress)
        : null;
      const addressValidError = found ? null : 'form.input.address.error';
      const validation =
        addressValidError || (apolloError && String(apolloError));
      setValidationError(validation);
      setIsAddressInvalid(Boolean(validation));
      return validation;
    },
    [apolloError, setIsAddressInvalid, setValidationError, address, addresses],
  );
  const onBlur = (e) => {
    setValidationError('');
    setIsAddressInvalid(false);
    if (
      containerRef.current &&
      e.relatedTarget &&
      !containerRef.current.contains(e.relatedTarget)
    ) {
      onCloseDropdown();
    }
  };
  const onSelectAddress = (item) => {
    setAddress(item);
    onCloseDropdown();
    onValidateAddress(item);
  };
  const onResetAddress = () => {
    setAddress('');
    setValidationError('');
    setIsAddressInvalid(false);
  };
  const handleSuggest = useCallback(
    debounce((value, onSuccess) => {
      onRequestAddress(value).then(onSuccess);
    }, 300),
    [onRequestAddress],
  );
  const onAddressChange = useCallback(
    (e) => {
      const { value } = e.target;
      setAddress(value);
      handleSuggest(value, () => {
        if (value.length >= MIN_ADDR_LENGTH && !opened) {
          onOpenDropdown(true);
        } else if (value.length < MIN_ADDR_LENGTH && opened) {
          onOpenDropdown(false);
        }
      });
      requestAnimationFrame(() => {
        // close if user clicked reset button
        if (!value) onOpenDropdown(false);
      });
    },
    [opened, onOpenDropdown, setAddress, handleSuggest],
  );
  useDefaultValue(() => {
    if (!initialAddress) return;
    handleSuggest(initialAddress, () => {
      setAddress(initialAddress);
    });
  }, [initialAddress, handleSuggest]);
  return {
    onBlur,
    onSelectAddress,
    onResetAddress,
    onAddressChange,
    opened,
    containerRef,
    addresses,
    loading,
    validationError,
    onOpenDropdown,
    onCloseDropdown,
    isAddressInvalid,
    address,
    onValidateAddress,
  };
}

export function useBedroom(initialBedrooms) {
  const t = useTranslations();
  const options = [
    { value: 0, label: t('studio') },
    { value: 1, label: '1 ' + t('bedroom') },
    { value: 2, label: '2 ' + t('bedrooms') },
    { value: 3, label: '3 ' + t('bedrooms') },
    { value: 4, label: '4 ' + t('bedrooms') },
    { value: 5, label: '5 ' + t('bedrooms') },
    { value: 6, label: '6+ ' + t('bedrooms') },
  ];
  const [isBedroomInvalid, setBedroomInvalid] = useState(false);
  const [bedroom, selectBedroom] = useState(null);
  useDefaultValue(() => {
    const option = options.find((o) => o.value === initialBedrooms);
    if (option) {
      selectBedroom(option);
    }
  }, [initialBedrooms]);
  const onValidateBedroom = useCallback(
    (actualBedroom = null) => {
      const value = actualBedroom || bedroom;
      const validation = Boolean(value);
      setBedroomInvalid(!validation);
      return !validation;
    },
    [bedroom, setBedroomInvalid],
  );
  const onSelectBedroom = useCallback(
    (item) => {
      selectBedroom(item);
      setBedroomInvalid(false);
      onValidateBedroom(item);
    },
    [onValidateBedroom],
  );
  const onBlur = useCallback(() => {
    setBedroomInvalid(false);
  }, [setBedroomInvalid]);
  return {
    bedroom,
    isBedroomInvalid,
    options,
    onSelectBedroom,
    onValidateBedroom,
    onBlur,
  };
}

function useDefaultValue(initFn = () => {}, deps = []) {
  const [called, setCalled] = useState(false);
  useEffect(() => {
    if (called) {
      return;
    }
    if (!deps.some((dep) => (typeof dep === 'number' ? true : Boolean(dep)))) {
      return;
    }
    setCalled(true);
    initFn();
  }, [...deps, called]);
}

export function useFormField({ onValidate: onValidateCustom } = {}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(false);
  const onChange = useCallback((event) => {
    setValue(event.target.value);
  }, []);
  const onReset = () => setValue('');
  const onValidate = useCallback(() => {
    if (!value) {
      setError('');
      setInvalid(true);
      return true;
    }
    const customError = onValidateCustom ? onValidateCustom(value) : null;
    if (customError) {
      setError(customError);
      setInvalid(Boolean(customError));
      return customError;
    }
    setError('');
    setInvalid(false);
    return '';
  }, [value, onValidateCustom]);
  const onBlur = useCallback(() => {
    setError('');
    setInvalid(false);
  }, [setError, setInvalid]);
  return {
    value,
    onChange,
    onReset,
    onValidate,
    error,
    invalid,
    onBlur,
  };
}

export function useFocusFirstError() {
  const errorContainerRef = useRef(null);
  function onFocusError() {
    if (!errorContainerRef.current) {
      return;
    }
    const dataTag = '[data-guestit-error="true"] ';
    const result = errorContainerRef.current.querySelector(
      `${dataTag} input, ${dataTag} textarea, ${dataTag} select`,
    );
    result?.focus();
  }
  return { containerRef: errorContainerRef, onFocusError };
}
